import axios from 'axios'
import { getToken, getRefreshToken } from '@/utils/auth'
import { Notification, Message } from 'element-ui'
import qs from 'qs'
import Config from '@/settings'
import store from '@/store'

const service = axios.create({
  // withCredentials: true,
  crossDomain: true,
  baseURL: process.env.NODE_ENV === 'production' ? process.env.VUE_APP_BASE_API : process.env.VUE_APP_BASE_API, // api 的 base_url
  timeout: Config.timeout // 请求超时时间
})

// request拦截器
service.interceptors.request.use(
  config => {
    if (getToken()) {
      config.headers.Authorization = getToken() // 让每个请求携带自定义token 请根据实际情况自行修改
    }
    // 判断是否是文件上传请求
    if (config.isUploadFile) {
      // 文件上传不需要 qs.stringify 处理
      config.headers['Content-Type'] = 'multipart/form-data'
    } else if (config.method === 'post') {
      // 普通 POST 请求处理数据
      config.data = qs.stringify({
        ...config.data
      })
      config.headers['Content-Type'] = 'application/x-www-form-urlencoded'
    }
    return config
  },
  error => {
    // Do something with request error
    Promise.reject(error)
  }
)
// response 拦截器
service.interceptors.response.use(
  response => {
    const code = response.data.code
    // 处理token过期或无效的情况
    if (code === 401 || code === 209) {
      const originalRequest = response.config
      // 如果是刷新token请求失败，直接登出
      if (isRefreshTokenRequest(originalRequest)) {
        return handleLogout()
      }

      // 如果refreshToken不存在，直接登出
      if (!hasRefreshToken()) {
        return handleLogout()
      }

      // 处理token刷新逻辑
      return handleTokenRefresh(originalRequest)
    }

    // 处理其他错误
    if (code !== 0) {
      Message.error({
        message: response.data.msg
      })
      return Promise.reject(response.data.msg)
    }
    return response.data
  },
  error => {
    // 统一处理请求失败的情况
    Notification.error({
      title: '请求失败',
      message: error.message || '网络错误',
      duration: 5000
    })
    return Promise.reject(error)
  }
)

// 判断是否是刷新token的请求
function isRefreshTokenRequest (config) {
  return config.url.includes('/refresh-token')
}

// 检查是否存在refreshToken
function hasRefreshToken () {
  return !!getRefreshToken()
}

// 处理登出逻辑
function handleLogout () {
  store.dispatch('user/logout')
  return Promise.reject(new Error('登录已过期，请重新登录'))
}

// 处理token刷新逻辑
function handleTokenRefresh (originalRequest) {
  // 如果没有刷新token的请求正在进行
  if (!window.isRefreshing) {
    window.isRefreshing = true

    return store.dispatch('user/refreshToken')
      .then(() => {
        // 刷新成功，重新发起原始请求
        originalRequest.headers.Authorization = getToken()
        window.isRefreshing = false
        executePendingRequests()
        // 确保 data 是对象而不是字符串
        if (typeof originalRequest.data === 'string') {
          try {
            originalRequest.data = qs.parse(originalRequest.data)
          } catch (e) {
            console.error('Failed to parse request data:', e)
          }
        }
        return service(originalRequest)
      })
      .catch(() => {
        // 刷新失败，登出
        window.isRefreshing = false
        return handleLogout()
      })
  }

  // 如果已经在刷新token，返回一个pending的promise
  return addRequestToQueue(originalRequest)
}

// 将请求加入等待队列
function addRequestToQueue (originalRequest) {
  return new Promise((resolve) => {
    window.tokenRefreshQueue = window.tokenRefreshQueue || []
    window.tokenRefreshQueue.push(() => {
      originalRequest.headers.Authorization = getToken()
      // 确保 data 是对象而不是字符串
      if (typeof originalRequest.data === 'string') {
        try {
          originalRequest.data = qs.parse(originalRequest.data)
        } catch (e) {
          console.error('Failed to parse request data:', e)
        }
      }
      resolve(service(originalRequest))
    })
  })
}

// 执行等待队列中的请求
function executePendingRequests () {
  if (window.tokenRefreshQueue) {
    window.tokenRefreshQueue.forEach(cb => cb())
    window.tokenRefreshQueue = []
  }
}

/**
 * 文件上传方法
 * @param {string} url - 上传接口地址
 * @param {FormData} formData - 包含文件的 FormData 对象
 * @param {Function} onUploadProgress - 上传进度回调函数（可选）
 * @returns {Promise}
 */
export function uploadFile (url, formData, onUploadProgress) {
  return service({
    url,
    method: 'post',
    data: formData,
    isUploadFile: true, // 标记为文件上传请求
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress: onUploadProgress || null // 支持上传进度
  })
}

/**
 * 文件下载方法
 * @param {string} url - 上传接口地址
 * @param {FormData} formData - 包含文件的 FormData 对象
 * @param {Function} onUploadProgress - 上传进度回调函数（可选）
 * @returns {Promise}
 */
export function downloadFile (url) {
  return service({
    url,
    method: 'get',
    responseType: 'blob'
  })
}

export default service
